import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Grid,
  Typography,
  Avatar,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  makeStyles,
  Button,
  LinearProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import PeopleIcon from '@material-ui/icons/People';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import PersonIcon from '@material-ui/icons/Person';
import AssessmentIcon from '@material-ui/icons/Assessment';
import ContactSupportIcon from '@material-ui/icons/ContactSupport';
import BarChartIcon from '@material-ui/icons/BarChart';
import CloseIcon from '@material-ui/icons/Close';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';

const useStyles = makeStyles((theme) => ({
  root: {
    background: 'transparent',
  },
  cardContent: {
    padding: '0px 8px 0 8px',
    marginTop: -3,
  },
  statCard: {
    borderRadius: 18,
    background: '#ffffff',
    border: '1px solid #d7e2ef',
    padding: 12,
    height: '100%',
  },
  statIcon: {
    height: 44,
    width: 44,
    backgroundColor: '#002D55',
    color: '#ffffff',
  },
  healthActive: {
    color: '#4caf50',
    fontWeight: 600,
  },
  healthWarning: {
    color: '#ff9800',
    fontWeight: 600,
  },
  tableHeader: {
    backgroundColor: '#F0F8FF',
    fontWeight: 600,
    color: '#002D55',
    fontSize: 13,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400,
  },
  primaryButton: {
    color: '#FFFFFF',
    backgroundColor: '#002D55',
    borderRadius: 18,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#fb741a',
      boxShadow: 'none',
    },
  },
  secondaryButton: {
    color: '#002D55',
    backgroundColor: '#e6eaf0',
    borderRadius: 12,
    boxShadow: 'none',
    fontWeight: 'bold',
    '&:hover': {
      color: '#fb741a',
      backgroundColor: '#e6eaf0',
      boxShadow: 'none',
    },
  },
  sectionTitle: {
    color: '#002D55',
    fontWeight: 700,
    fontSize: 16,
  },
  bodyText: {
    color: '#5f6c7b',
    fontSize: 13,
  },
  engagementBar: {
    height: 8,
    borderRadius: 4,
  },
  chartBox: {
    borderRadius: 10,
    border: '1px solid #d7e2ef',
    background: '#f7faff',
    padding: 12,
  },
  statLabelRow: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },
  helpIconButton: {
    padding: 0,
    marginLeft: 2,
    color: '#6b7a90',
    '&:hover': {
      color: '#FF7F27',
      backgroundColor: 'transparent',
    },
  },
  helpIcon: {
    fontSize: 16,
  },
  orangeFocused: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 14,
      backgroundColor: '#ffffff',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderRadius: 14,
      borderColor: '#dddddd',
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink:not(.Mui-focused)': {
      color: '#002D55',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
    '& .MuiInputLabel-outlined.Mui-focused': {
      color: '#FF7F27',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
    [theme.breakpoints.down('md')]: {
      '& .MuiOutlinedInput-input': {
        fontSize: 'clamp(0.68rem, 1vw, 0.82rem)',
      },
      '& .MuiInputLabel-root': {
        fontSize: 'clamp(0.68rem, 1vw, 0.82rem)',
      },
      '& .MuiInputLabel-shrink': {
        fontSize: 'clamp(0.62rem, 0.9vw, 0.72rem)',
      },
      '& .MuiFormHelperText-root': {
        fontSize: 'clamp(0.62rem, 0.85vw, 0.7rem)',
      },
    },
    [theme.breakpoints.down('sm')]: {
      '& .MuiOutlinedInput-root': {
        borderRadius: 12,
      },
      '& .MuiOutlinedInput-input': {
        paddingTop: 10,
        paddingBottom: 10,
        fontSize: 'clamp(0.84rem, 3.1vw, 0.92rem)',
      },
      '& .MuiInputLabel-root': {
        fontSize: 'clamp(0.84rem, 3.1vw, 0.92rem)',
      },
      '& .MuiInputLabel-shrink': {
        fontSize: 'clamp(0.72rem, 2.5vw, 0.78rem)',
      },
      '& .MuiFormHelperText-root': {
        fontSize: 'clamp(0.68rem, 2.2vw, 0.72rem)',
      },
    },
  },
  dialogPaper: {
    borderRadius: 18,
    overflow: 'hidden',
    boxShadow: '0 18px 40px rgba(0,45,85,0.18)',
    [theme.breakpoints.down('sm')]: {
      borderRadius: 0,
      boxShadow: 'none',
      width: '100%',
      maxWidth: '100%',
      margin: 0,
    },
  },
  dialogTitle: {
    borderBottom: '1px solid #eeeeee',
    padding: '16px 24px',
    backgroundColor: '#ffffff',
    [theme.breakpoints.down('sm')]: {
      padding: '12px 16px',
    },
  },
  dialogTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
      alignItems: 'flex-start',
    },
  },
  dialogTitleText: {
    flex: 1,
    color: '#002D55',
    fontWeight: 600,
    fontSize: 'clamp(0.95rem, calc(4px + 0.72vw), 1.15rem)',
    textTransform: 'uppercase',
    [theme.breakpoints.down('md')]: {
      fontSize: 'clamp(0.82rem, 1.3vw, 1rem)',
      lineHeight: 1.15,
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: 'clamp(0.75rem, 1.04vw, 0.95rem)',
      lineHeight: 1.2,
    },
  },
  closeButton: {
    color: '#64748B',
    padding: 6,
    '&:hover': {
      backgroundColor: '#edf5fc',
    },
  },
  dialogContent: {
    padding: '24px !important',
    backgroundColor: '#e6eaf0',
    [theme.breakpoints.down('md')]: {
      padding: '20px !important',
    },
    [theme.breakpoints.down('sm')]: {
      padding: '16px !important',
    },
  },
  dialogActions: {
    padding: 16,
    gap: 8,
    justifyContent: 'flex-end',
    borderTop: '1px solid #eeeeee',
    backgroundColor: '#ffffff',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      padding: 12,
    },
  },
  dialogActionButton: {
    borderRadius: 50,
  },
}));

const CRMProviderDashboard = ({ analyticsKpis = null }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const token = useSelector((state) => state.auth.token);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const backendBaseUrl = process.env.REACT_APP_BACKEND_URL || 'https://api.virtus-one.com';

  // State for provider data
  const [metrics, setMetrics] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [clinicOverview, setClinicOverview] = useState(null);
  const [clinicOverviewLoading, setClinicOverviewLoading] = useState(false);
  const [clinicOverviewStaffId, setClinicOverviewStaffId] = useState('all');
  const [staffPerformance, setStaffPerformance] = useState([]);
  const [staffPerformanceClinicNps, setStaffPerformanceClinicNps] = useState(null);
  const [staffPerformanceComplaintLabel, setStaffPerformanceComplaintLabel] = useState('All complaints');
  const [staffComplaintKey, setStaffComplaintKey] = useState('all');
  const [staffComplaintOptions, setStaffComplaintOptions] = useState([{ primary_complaint_key: 'all', primary_complaint_label: 'All complaints', count: null }]);
  const [staffComplaintBreakdown, setStaffComplaintBreakdown] = useState([]);
  const [staffNpsReport, setStaffNpsReport] = useState(null);
  const [staffNpsPeriod, setStaffNpsPeriod] = useState('last_90_days');
  const [staffNpsLoading, setStaffNpsLoading] = useState(false);
  const [staffNpsError, setStaffNpsError] = useState('');
  const [patientAcquisition, setPatientAcquisition] = useState([]);
  const [supportTickets, setSupportTickets] = useState([]);
  const [staffPerformanceLanguage, setStaffPerformanceLanguage] = useState('en');
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Support ticket dialog
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [newTicket, setNewTicket] = useState({
    subject: '',
    description: '',
    priority: 'medium',
    category: 'general',
  });

  useEffect(() => {
    if (isLoggedIn && token) {
      fetchAllData();
    }
  }, [isLoggedIn, token]);

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        fetchMetrics(),
        fetchRevenue(),
        fetchPatientAcquisition(),
        fetchSupportTickets(),
      ]);
    } catch (err) {
      setError(err.message || 'Failed to fetch CRM data');
    } finally {
      setLoading(false);
    }
  };

  const fetchMetrics = async () => {
    try {
      const response = await fetch(`${backendBaseUrl}/api/crm/provider/metrics/`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const text = await response.text();
        console.error(`Metrics API Error (${response.status}):`, text.substring(0, 200));
        throw new Error(`Failed to fetch metrics: ${response.status}`);
      }
      const data = await response.json();
      setMetrics(data);
    } catch (err) {
      console.error('Metrics fetch error:', err);
      setError(`Unable to load metrics. Please ensure the backend CRM endpoints are running. Error: ${err.message}`);
    }
  };

  const fetchRevenue = async () => {
    try {
      const response = await fetch(`${backendBaseUrl}/api/crm/provider/revenue/`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const text = await response.text();
        console.error(`Revenue API Error (${response.status}):`, text.substring(0, 200));
        throw new Error(`Failed to fetch revenue: ${response.status}`);
      }
      const data = await response.json();
      setRevenue(data);
    } catch (err) {
      console.error('Revenue fetch error:', err);
    }
  };

  const fetchClinicOverview = useCallback(async (staffMemberId = 'all') => {
    setClinicOverviewLoading(true);
    try {
      const query = staffMemberId && staffMemberId !== 'all'
        ? `?staff_member_id=${encodeURIComponent(staffMemberId)}`
        : '';
      const response = await fetch(`${backendBaseUrl}/api/crm/provider/clinic-overview/${query}`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const text = await response.text();
        console.error(`Clinic Overview API Error (${response.status}):`, text.substring(0, 200));
        throw new Error(`Failed to fetch clinic overview: ${response.status}`);
      }
      const data = await response.json();
      setClinicOverview(data);
    } catch (err) {
      console.error('Clinic overview fetch error:', err);
    } finally {
      setClinicOverviewLoading(false);
    }
  }, [backendBaseUrl, token]);

  useEffect(() => {
    if (isLoggedIn && token) {
      fetchClinicOverview(clinicOverviewStaffId);
    }
  }, [isLoggedIn, token, clinicOverviewStaffId, fetchClinicOverview]);

  const fetchStaffPerformance = async (period = staffNpsPeriod, complaintKey = staffComplaintKey) => {
    try {
      const query = new URLSearchParams();
      query.set('period_type', period);
      if (complaintKey && complaintKey !== 'all') {
        query.set('primary_complaint_key', complaintKey);
      }
      const response = await fetch(`${backendBaseUrl}/api/crm/provider/staff-performance/?${query.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const text = await response.text();
        console.error(`Staff Performance API Error (${response.status}):`, text.substring(0, 200));
        throw new Error(`Failed to fetch staff performance: ${response.status}`);
      }
      const data = await response.json();
      setStaffPerformance(data.staff || []);
      setStaffPerformanceClinicNps(data.clinic_avg_nps ?? null);
      setStaffPerformanceComplaintLabel(data.selected_complaint_label || 'All complaints');
      setStaffComplaintBreakdown((data.complaint_breakdown || []).map((item) => ({
        primary_complaint_key: item.primary_complaint_key,
        primary_complaint_label: item.primary_complaint_label,
        count: item.count,
        avg_nps: item.avg_nps,
      })));
      setStaffComplaintOptions([
        { primary_complaint_key: 'all', primary_complaint_label: 'All complaints', count: null },
        ...((data.complaint_options || []).map((item) => ({
          primary_complaint_key: item.primary_complaint_key,
          primary_complaint_label: item.primary_complaint_label,
          count: item.count,
        }))),
      ]);
    } catch (err) {
      console.error('Staff performance fetch error:', err);
    }
  };

  useEffect(() => {
    if (isLoggedIn && token) {
      fetchStaffPerformance(staffNpsPeriod, staffComplaintKey);
    }
  }, [isLoggedIn, token, staffNpsPeriod, staffComplaintKey]);

  const fetchPatientAcquisition = async () => {
    try {
      const response = await fetch(`${backendBaseUrl}/api/crm/provider/patient-acquisition/`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const text = await response.text();
        console.error(`Patient Acquisition API Error (${response.status}):`, text.substring(0, 200));
        throw new Error(`Failed to fetch patient acquisition: ${response.status}`);
      }
      const data = await response.json();
      setPatientAcquisition(data.monthly_trends || []);
    } catch (err) {
      console.error('Patient acquisition fetch error:', err);
    }
  };

  const fetchSupportTickets = async () => {
    try {
      const response = await fetch(`${backendBaseUrl}/api/crm/provider/support-tickets/`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const text = await response.text();
        console.error(`Support Tickets API Error (${response.status}):`, text.substring(0, 200));
        throw new Error(`Failed to fetch support tickets: ${response.status}`);
      }
      const data = await response.json();
      setSupportTickets(data.results || data);
    } catch (err) {
      console.error('Support tickets fetch error:', err);
    }
  };

  const handleCreateTicket = async () => {
    try {
      const response = await fetch(`${backendBaseUrl}/api/crm/provider/support-tickets/`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newTicket),
      });
      if (!response.ok) throw new Error('Failed to create ticket');
      
      // Reset form and close dialog
      setNewTicket({
        subject: '',
        description: '',
        priority: 'medium',
        category: 'general',
      });
      setTicketDialogOpen(false);
      
      // Refresh tickets
      await fetchSupportTickets();
    } catch (err) {
      console.error('Create ticket error:', err);
      alert('Failed to create support ticket');
    }
  };

  const formatCurrency = (amount, currency = 'DKK') => {
    return new Intl.NumberFormat('da-DK', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;

  const staffPerformanceText = {
    en: {
      title: 'Staff Performance',
      name: 'Name',
      appointments: 'Appointments',
      revenue: 'Revenue',
      hourlyPay: 'Hourly pay',
      revenuePerHour: 'Revenue per worked hour',
      avgNps: 'Avg. NPS',
      clinicNps: 'Clinic NPS',
      complaint: 'Complaint',
      allComplaints: 'All complaints',
      complaintBreakdown: 'Complaint breakdown',
      staffComplaintBreakdown: 'Staff complaint split',
      measurementCount: 'measurements',
      completionRate: 'Completion Rate',
      noData: 'No staff data available',
      noComplaintBreakdown: 'No segmented NPS data',
      npsPeriod: 'NPS period',
      periodLastMonth: 'Last month',
      periodLast90Days: 'Last 90 days',
      periodLastYear: 'Last year',
    },
    da: {
      title: 'Medarbejderperformance',
      name: 'Navn',
      appointments: 'Aftaler',
      revenue: 'Omsætning',
      hourlyPay: 'Timeløn',
      revenuePerHour: 'Omsætning per time',
      avgNps: 'Gns. NPS',
      clinicNps: 'Klinik-NPS',
      complaint: 'Klagesegment',
      allComplaints: 'Alle klager',
      complaintBreakdown: 'Klagesegmenter',
      staffComplaintBreakdown: 'Klagesplit pr. medarbejder',
      measurementCount: 'målinger',
      completionRate: 'Fuldførelsesgrad',
      noData: 'Ingen medarbejderdata tilgængelig',
      noComplaintBreakdown: 'Ingen segmenteret NPS-data',
      npsPeriod: 'NPS-periode',
      periodLastMonth: 'Sidste måned',
      periodLast90Days: 'Sidste 90 dage',
      periodLastYear: 'Sidste år',
    },
  }[staffPerformanceLanguage];

  const getStaffAnalyticsDetails = (staff) => {
    const staffProfiles = analyticsKpis?.staffProfiles || [];
    const matchedProfile = staffProfiles.find((profile) => String(profile.user_id || profile.id || '') === String(staff.staff_id));
    const weeklyWorkHours = Number(matchedProfile?.weekly_work_hours || staff.weekly_work_hours || 37.5) || 37.5;
    const monthlySalaryAmount = Number(matchedProfile?.monthly_salary_amount || staff.monthly_salary_amount || 0) || 0;
    const monthlyExpectedHours = weeklyWorkHours > 0 ? (weeklyWorkHours * 52) / 12 : 0;
    const hourlyPay = monthlySalaryAmount > 0 && monthlyExpectedHours > 0 ? monthlySalaryAmount / monthlyExpectedHours : null;
    const revenuePerWorkedHour = monthlyExpectedHours > 0 ? Number(staff.total_revenue || 0) / monthlyExpectedHours : null;
    return {
      weeklyWorkHours,
      monthlySalaryAmount,
      monthlyExpectedHours,
      hourlyPay,
      revenuePerWorkedHour,
    };
  };

  const renderHelpIcon = (title, label = 'More information') => (
    <Tooltip title={title} arrow placement="top">
      <IconButton aria-label={label} className={classes.helpIconButton} size="small">
        <HelpOutlineIcon className={classes.helpIcon} />
      </IconButton>
    </Tooltip>
  );

  const getOverviewHelpText = (row) => {
    switch (row.category) {
      case 'Gender':
        return `Shows the patient ${row.segment.toLowerCase()} share in the selected scope.`;
      case 'Age group':
        return `Shows how many patients in the selected scope fall into ${row.segment} age band.`;
      case 'Average revenue per age group':
        return `Average revenue per patient in the ${row.segment} age band.`;
      case 'Revenue per service':
        return `Total paid revenue generated by ${row.segment}.`;
      case 'Average revenue per patient/treatment course':
        return 'Average revenue generated per patient treatment course in the selected scope.';
      case 'Consultation format':
        return `${row.segment} appointments as a share of all consultations in the selected scope.`;
      case 'Assessment intake':
        return 'Percentage of initial intake workflows completed before the appointment.';
      case 'Follow-up questionnaire':
        return 'Percentage of follow-up questionnaires completed before the consultation.';
      default:
        return 'Statistic for the selected clinic scope.';
    }
  };

  const getServiceHelpText = (service) => `${service.service_name} performance in the selected scope: appointment volume, revenue, completion rate, format split, and unique patients.`;

  const overviewRows = clinicOverview ? [
    {
      category: 'Gender',
      segment: 'Male',
      count: clinicOverview.gender?.male?.count || 0,
      percent: formatPercent(clinicOverview.gender?.male?.percent),
      value: `${clinicOverview.gender?.classified_total || 0} classified patients`,
    },
    {
      category: 'Gender',
      segment: 'Female',
      count: clinicOverview.gender?.female?.count || 0,
      percent: formatPercent(clinicOverview.gender?.female?.percent),
      value: clinicOverview.gender?.unknown_count ? `${clinicOverview.gender.unknown_count} unknown` : '—',
    },
    ...(clinicOverview.age_groups || []).map((ageGroup) => ({
      category: 'Age group',
      segment: ageGroup.label,
      count: ageGroup.count || 0,
      percent: formatPercent(ageGroup.percent),
      value: 'Patients with known age',
    })),
    ...(clinicOverview.age_group_revenue || []).map((ageGroup) => ({
      category: 'Average revenue per age group',
      segment: ageGroup.label,
      count: ageGroup.patient_count || 0,
      percent: formatPercent(ageGroup.percent_of_patients),
      value: formatCurrency(ageGroup.average_revenue || 0, ageGroup.currency || 'DKK'),
    })),
    ...(clinicOverview.revenue_by_service || []).map((service) => ({
      category: 'Revenue per service',
      segment: service.service_name,
      count: service.appointments || 0,
      percent: '—',
      value: `${formatCurrency(service.revenue || 0, service.currency || 'DKK')} revenue`,
    })),
    {
      category: 'Average revenue per patient/treatment course',
      segment: clinicOverview.average_revenue_per_patient_treatment_course?.currency || '—',
      count: clinicOverview.average_revenue_per_patient_treatment_course?.total_courses || 0,
      percent: '—',
      value: clinicOverview.average_revenue_per_patient_treatment_course?.currency
        ? formatCurrency(
            clinicOverview.average_revenue_per_patient_treatment_course.average_revenue || 0,
            clinicOverview.average_revenue_per_patient_treatment_course.currency,
          )
        : '—',
    },
    {
      category: 'Consultation format',
      segment: 'Video',
      count: clinicOverview.consultation_format?.video_consultation?.count || 0,
      percent: formatPercent(clinicOverview.consultation_format?.video_consultation?.percent),
      value: `${clinicOverview.consultation_format?.total || 0} total consultations`,
    },
    {
      category: 'Consultation format',
      segment: 'In-person',
      count: clinicOverview.consultation_format?.in_person?.count || 0,
      percent: formatPercent(clinicOverview.consultation_format?.in_person?.percent),
      value: `${clinicOverview.consultation_format?.total || 0} total consultations`,
    },
    {
      category: 'Assessment intake',
      segment: 'Initial intake completed before appointment',
      count: clinicOverview.intake_completion?.completed || 0,
      percent: formatPercent(clinicOverview.intake_completion?.percent),
      value: `${clinicOverview.intake_completion?.total || 0} workflows`,
    },
    {
      category: 'Follow-up questionnaire',
      segment: 'Completed before consultation',
      count: clinicOverview.follow_up_questionnaire?.completed_before_consultation || 0,
      percent: formatPercent(clinicOverview.follow_up_questionnaire?.percent),
      value: `${clinicOverview.follow_up_questionnaire?.total || 0} workflows`,
    },
  ] : [];

  const serviceRows = clinicOverview?.service_statistics || [];

  const getHealthColor = (status) => {
    switch (status) {
      case 'active':
        return '#4caf50';
      case 'inactive_30d':
        return '#ff9800';
      case 'inactive_60d':
        return '#f44336';
      case 'at_risk':
        return '#d32f2f';
      default:
        return '#9e9e9e';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical':
        return '#d32f2f';
      case 'high':
        return '#f57c00';
      case 'medium':
        return '#fbc02d';
      case 'low':
        return '#689f38';
      default:
        return '#9e9e9e';
    }
  };

  if (!isLoggedIn || !token) {
    return (
      <Box p={3}>
        <Typography color="error">Please log in to view your clinic analytics</Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress size={60} style={{ color: '#002D55' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">Error: {error}</Typography>
        <Button onClick={fetchAllData} variant="contained" className={classes.primaryButton} style={{ marginTop: 16 }}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box className={classes.cardContent}>
        <Divider style={{ marginTop: 8, marginBottom: 16 }} />
        <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <AssessmentIcon style={{ color: '#002D55' }} />
          <Typography className={classes.sectionTitle} style={{ fontSize: 18, marginBottom: 0 }}>Clinic Patient Overview</Typography>
        </Box>
        <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <Typography className={classes.bodyText}>
            {clinicOverview?.scope?.label ? `Scope: ${clinicOverview.scope.label}` : 'Scope: All staff'}
          </Typography>
          <TextField
            select
            variant="outlined"
            size="small"
            label="Staff scope"
            value={clinicOverviewStaffId}
            onChange={(event) => setClinicOverviewStaffId(event.target.value)}
            className={classes.orangeFocused}
            style={{ minWidth: 220 }}
          >
            <MenuItem value="all">All staff</MenuItem>
            {staffPerformance.map((staff, index) => (
              <MenuItem key={`${staff.staff_name || 'staff'}-${index}`} value={staff.staff_id || staff.staff_member_id || staff.user_id || staff.staff_name}>
                {staff.staff_name || `Staff ${index + 1}`}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <TableContainer component='div' style={{ borderRadius: 18, overflow: 'hidden', border: '1px solid #d7e2ef' }}>
          <Table>
            <TableHead>
              <TableRow style={{ backgroundColor: '#F0F8FF' }}>
                <TableCell className={classes.tableHeader} style={{ fontSize: 13 }}>Category</TableCell>
                <TableCell className={classes.tableHeader} style={{ fontSize: 13 }}>Segment</TableCell>
                <TableCell className={classes.tableHeader} style={{ fontSize: 13 }} align="right">Count</TableCell>
                <TableCell className={classes.tableHeader} style={{ fontSize: 13 }} align="right">Percent</TableCell>
                <TableCell className={classes.tableHeader} style={{ fontSize: 13 }}>Value / Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clinicOverviewLoading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <CircularProgress size={24} style={{ color: '#002D55' }} />
                  </TableCell>
                </TableRow>
              ) : overviewRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography className={classes.bodyText}>No clinic overview data available</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                overviewRows.map((row, index) => (
                  <TableRow key={`${row.category}-${row.segment}-${index}`} hover>
                    <TableCell>
                      <span className={classes.statLabelRow}>
                        <Typography style={{ fontSize: 13, color: '#002D55', fontWeight: 600 }}>{row.category}</Typography>
                        {renderHelpIcon(getOverviewHelpText(row), `${row.category} help`)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Typography style={{ fontSize: 13, color: '#324158' }}>{row.segment}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography className={classes.bodyText}>{row.count}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography style={{ fontSize: 13, color: '#002D55', fontWeight: 600 }}>{row.percent}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography className={classes.bodyText}>{row.value}</Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider style={{ marginTop: 24, marginBottom: 16 }} />
        <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <BarChartIcon style={{ color: '#002D55' }} />
          <Typography className={classes.sectionTitle} style={{ fontSize: 18, marginBottom: 0 }}>All Services Statistics</Typography>
        </Box>
        <TableContainer component='div' style={{ borderRadius: 18, overflow: 'hidden', border: '1px solid #d7e2ef' }}>
          <Table size="small">
            <TableHead>
              <TableRow style={{ backgroundColor: '#F0F8FF' }}>
                <TableCell className={classes.tableHeader} style={{ fontSize: 13 }}>Service</TableCell>
                <TableCell className={classes.tableHeader} style={{ fontSize: 13 }} align="right">Appointments</TableCell>
                <TableCell className={classes.tableHeader} style={{ fontSize: 13 }} align="right">% of scope</TableCell>
                <TableCell className={classes.tableHeader} style={{ fontSize: 13 }} align="right">Revenue</TableCell>
                <TableCell className={classes.tableHeader} style={{ fontSize: 13 }} align="right">Completion</TableCell>
                <TableCell className={classes.tableHeader} style={{ fontSize: 13 }} align="right">Video / In-person</TableCell>
                <TableCell className={classes.tableHeader} style={{ fontSize: 13 }} align="right">Patients</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clinicOverviewLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress size={24} style={{ color: '#002D55' }} />
                  </TableCell>
                </TableRow>
              ) : serviceRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography className={classes.bodyText}>No service statistics available</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                serviceRows.map((service) => (
                  <TableRow key={service.service_id || service.service_name} hover>
                    <TableCell>
                      <span className={classes.statLabelRow}>
                        <Typography style={{ fontSize: 13, color: '#002D55', fontWeight: 600 }}>{service.service_name}</Typography>
                        {renderHelpIcon(getServiceHelpText(service), `${service.service_name} help`)}
                      </span>
                      <Typography className={classes.bodyText} style={{ fontSize: 12 }}>
                        {service.duration_minutes ? `${service.duration_minutes} min` : 'Duration n/a'}
                        {service.service_type ? ` • ${service.service_type}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography className={classes.bodyText}>{service.appointments || 0}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography style={{ fontSize: 13, color: '#002D55', fontWeight: 600 }}>{formatPercent(service.appointment_percent)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography className={classes.bodyText}>{formatCurrency(service.revenue || 0, service.currency || 'DKK')}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography style={{ fontSize: 13, color: '#002D55', fontWeight: 600 }}>{formatPercent(service.completion_rate)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography className={classes.bodyText}>
                        {service.video_consultations || 0} / {service.in_person_consultations || 0}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography className={classes.bodyText}>{service.unique_patients || 0}</Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Top description row */}
        <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Typography className={classes.bodyText}>
            Overview of clinic engagement, revenue, staff performance, and support.
          </Typography>
          <Chip
            label={`Health: ${metrics?.health_status?.replace('_', ' ').toUpperCase() || '—'}`}
            style={{ backgroundColor: '#e9f2fb', color: '#002D55', fontWeight: 700 }}
          />
        </Box>
        <Divider style={{ marginTop: 12, marginBottom: 16 }} />

        {/* Key Metrics */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Box className={classes.statCard}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Box className={classes.statLabelRow}>
                    <Typography className={classes.bodyText}>Engagement Score</Typography>
                    {renderHelpIcon('A combined health indicator based on engagement, revenue, support usage, and clinic activity.', 'Engagement Score help')}
                  </Box>
                  <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 22, marginTop: 2 }}>
                    {metrics?.engagement_score || 0}/100
                  </Typography>
                  <Typography variant="caption" className={metrics?.health_status === 'active' ? classes.healthActive : classes.healthWarning}>
                    {metrics?.health_status?.replace('_', ' ').toUpperCase()}
                  </Typography>
                </Box>
                <Avatar className={classes.statIcon}>
                  <AssessmentIcon />
                </Avatar>
              </Box>
              <LinearProgress
                variant="determinate"
                value={metrics?.engagement_score || 0}
                className={classes.engagementBar}
                style={{ marginTop: 16, backgroundColor: '#e0e0e0' }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box className={classes.statCard}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography className={classes.bodyText}>Total Revenue</Typography>
                  <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 22, marginTop: 2 }}>
                    {revenue?.total_revenue ? formatCurrency(revenue.total_revenue) : '—'}
                  </Typography>
                  <Typography className={classes.bodyText}>
                    {revenue?.total_appointments || 0} appointments
                  </Typography>
                </Box>
                <Avatar className={classes.statIcon}>
                  <AttachMoneyIcon />
                </Avatar>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box className={classes.statCard}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography className={classes.bodyText}>Total Patients</Typography>
                  <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 22, marginTop: 2 }}>
                    {metrics?.total_patients || 0}
                  </Typography>
                  <Typography className={classes.bodyText}>
                    {patientAcquisition[0]?.new_patients || 0} new this month
                  </Typography>
                </Box>
                <Avatar className={classes.statIcon}>
                  <PeopleIcon />
                </Avatar>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box className={classes.statCard}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography className={classes.bodyText}>Staff Members</Typography>
                  <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 22, marginTop: 2 }}>
                    {metrics?.total_staff || 0}
                  </Typography>
                  <Typography className={classes.bodyText}>Active team</Typography>
                </Box>
                <Avatar className={classes.statIcon}>
                  <PersonIcon />
                </Avatar>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Revenue Analysis */}
        <Divider style={{ marginTop: 24, marginBottom: 16 }} />
        <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <BarChartIcon style={{ color: '#002D55' }} />
          <Typography className={classes.sectionTitle} style={{ fontSize: 18, marginBottom: 0 }}>Revenue Analysis</Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Box className={classes.statCard}>
              <Typography style={{ color: '#002D55', fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
                Revenue by Status
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography className={classes.bodyText}>Paid</Typography>
                  <Typography style={{ color: '#4caf50', fontWeight: 700, fontSize: 18, marginTop: 2 }}>
                    {revenue?.paid_revenue ? formatCurrency(revenue.paid_revenue) : '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography className={classes.bodyText}>Pending</Typography>
                  <Typography style={{ color: '#FF7F27', fontWeight: 700, fontSize: 18, marginTop: 2 }}>
                    {revenue?.pending_revenue ? formatCurrency(revenue.pending_revenue) : '—'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box className={classes.statCard}>
              <Typography style={{ color: '#002D55', fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
                Completion Rate
              </Typography>
              <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 18, marginTop: 2 }}>
                {revenue?.completion_rate ? `${revenue.completion_rate.toFixed(1)}%` : '—'}
              </Typography>
              <Typography className={classes.bodyText}>
                {revenue?.completed_appointments || 0} / {revenue?.total_appointments || 0} appointments
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {revenue?.revenue_30d && revenue.revenue_30d.length > 0 && (
          <Box mt={2}>
            <Typography className={classes.sectionTitle} style={{ marginBottom: 8 }}>
              30-Day Revenue Trend
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              {revenue.revenue_30d.slice(-7).map((day, index) => (
                <Box key={index} flex={1} minWidth={60} textAlign="center">
                  <Typography className={classes.bodyText}>
                    {new Date(day.date).toLocaleDateString('da-DK', { day: '2-digit', month: '2-digit' })}
                  </Typography>
                  <Box mt={1} height={60} display="flex" alignItems="flex-end" justifyContent="center">
                    <Box
                      width="100%"
                      height={`${Math.max((day.total / Math.max(...revenue.revenue_30d.map(d => d.total))) * 100, 5)}%`}
                      bgcolor="#FF7F27"
                      borderRadius={1}
                    />
                  </Box>
                  <Typography className={classes.bodyText} style={{ fontWeight: 600 }}>
                    {formatCurrency(day.total)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Staff Performance */}
        <Divider style={{ marginTop: 24, marginBottom: 16 }} />
        <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <TrendingUpIcon style={{ color: '#002D55' }} />
            <Typography className={classes.sectionTitle} style={{ fontSize: 18, marginBottom: 0 }}>{staffPerformanceText.title}</Typography>
          </Box>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <TextField
              select
              size="small"
              variant="outlined"
              label={staffPerformanceText.npsPeriod}
              value={staffNpsPeriod}
              onChange={(event) => setStaffNpsPeriod(event.target.value)}
              style={{ minWidth: 170 }}
              className={classes.orangeFocused}
            >
              <MenuItem value="last_month">{staffPerformanceText.periodLastMonth}</MenuItem>
              <MenuItem value="last_90_days">{staffPerformanceText.periodLast90Days}</MenuItem>
              <MenuItem value="last_year">{staffPerformanceText.periodLastYear}</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              variant="outlined"
              label={staffPerformanceText.complaint}
              value={staffComplaintKey}
              onChange={(event) => setStaffComplaintKey(event.target.value)}
              style={{ minWidth: 230 }}
              className={classes.orangeFocused}
            >
              {staffComplaintOptions.map((option) => (
                <MenuItem key={option.primary_complaint_key} value={option.primary_complaint_key}>
                  {option.primary_complaint_key === 'all'
                    ? staffPerformanceText.allComplaints
                    : `${option.primary_complaint_label}${typeof option.count === 'number' ? ` (${option.count})` : ''}`}
                </MenuItem>
              ))}
            </TextField>
            <Box style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1px solid #d7e2ef', borderRadius: 999, padding: 4, background: '#fff' }}>
              <Button
                size="small"
                variant={staffPerformanceLanguage === 'en' ? 'contained' : 'text'}
                onClick={() => setStaffPerformanceLanguage('en')}
                style={{ minWidth: 56, borderRadius: 999, backgroundColor: staffPerformanceLanguage === 'en' ? '#002D55' : 'transparent', color: staffPerformanceLanguage === 'en' ? '#fff' : '#002D55', fontSize: 11, fontWeight: 700 }}
              >
                GB
              </Button>
              <Button
                size="small"
                variant={staffPerformanceLanguage === 'da' ? 'contained' : 'text'}
                onClick={() => setStaffPerformanceLanguage('da')}
                style={{ minWidth: 56, borderRadius: 999, backgroundColor: staffPerformanceLanguage === 'da' ? '#002D55' : 'transparent', color: staffPerformanceLanguage === 'da' ? '#fff' : '#002D55', fontSize: 11, fontWeight: 700 }}
              >
                DKK
              </Button>
            </Box>
          </Box>
        </Box>
        <Box style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 12 }}>
          <Box style={{ minWidth: 190, borderRadius: 16, border: '1px solid #d7e2ef', background: 'linear-gradient(135deg, #ffffff 0%, #f5f9ff 100%)', padding: '12px 16px' }}>
            <Typography style={{ color: '#4b5f78', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
              {staffPerformanceText.clinicNps}
            </Typography>
            <Typography style={{ color: '#002D55', fontSize: 24, fontWeight: 800, lineHeight: 1.1, marginTop: 4 }}>
              {staffPerformanceClinicNps != null ? Number(staffPerformanceClinicNps).toFixed(1) : '—'}
            </Typography>
            <Typography className={classes.bodyText} style={{ marginTop: 4 }}>
              {staffPerformanceComplaintLabel} • {staffPerformanceText.npsPeriod}: {staffPerformanceText[staffNpsPeriod === 'last_month' ? 'periodLastMonth' : staffNpsPeriod === 'last_year' ? 'periodLastYear' : 'periodLast90Days']}
            </Typography>
          </Box>
        </Box>
        <Box style={{ marginBottom: 16 }}>
          <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
            <Typography className={classes.sectionTitle} style={{ fontSize: 15, marginBottom: 0 }}>
              {staffPerformanceText.complaintBreakdown}
            </Typography>
            <Typography className={classes.bodyText} style={{ marginBottom: 0 }}>
              {staffPerformanceText.allComplaints} • {staffPerformanceText.npsPeriod}: {staffPerformanceText[staffNpsPeriod === 'last_month' ? 'periodLastMonth' : staffNpsPeriod === 'last_year' ? 'periodLastYear' : 'periodLast90Days']}
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {staffComplaintBreakdown.length === 0 ? (
              <Grid item xs={12}>
                <Box className={classes.statCard} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 84 }}>
                  <Typography className={classes.bodyText}>{staffPerformanceText.noData}</Typography>
                </Box>
              </Grid>
            ) : (
              staffComplaintBreakdown.map((bucket) => {
                const isActive = staffComplaintKey === bucket.primary_complaint_key;
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={bucket.primary_complaint_key}>
                    <Box
                      className={classes.statCard}
                      onClick={() => setStaffComplaintKey(bucket.primary_complaint_key)}
                      style={{
                        cursor: 'pointer',
                        borderColor: isActive ? '#fb741a' : '#d7e2ef',
                        boxShadow: isActive ? '0 10px 22px rgba(251,116,26,0.12)' : 'none',
                        transform: isActive ? 'translateY(-1px)' : 'none',
                        transition: 'all 140ms ease',
                      }}
                    >
                      <Typography style={{ color: '#4b5f78', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                        {bucket.primary_complaint_label}
                      </Typography>
                      <Typography style={{ color: '#002D55', fontSize: 24, fontWeight: 800, lineHeight: 1.1, marginTop: 4 }}>
                        {bucket.avg_nps != null ? Number(bucket.avg_nps).toFixed(1) : '—'}
                      </Typography>
                      <Typography className={classes.bodyText} style={{ marginTop: 4 }}>
                        {bucket.count || 0} {staffPerformanceText.measurementCount}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })
            )}
          </Grid>
        </Box>
        <TableContainer component='div' style={{ borderRadius: 18, overflow: 'hidden', border: '1px solid #d7e2ef' }}>
          <Table>
            <TableHead>
              <TableRow style={{ backgroundColor: '#F0F8FF' }}>
                <TableCell className={classes.tableHeader} style={{ fontSize: 13 }}>{staffPerformanceText.name}</TableCell>
                <TableCell className={classes.tableHeader} style={{ fontSize: 13 }} align="right">{staffPerformanceText.appointments}</TableCell>
                <TableCell className={classes.tableHeader} style={{ fontSize: 13 }} align="right">{staffPerformanceText.revenue}</TableCell>
                <TableCell className={classes.tableHeader} style={{ fontSize: 13 }} align="right">{staffPerformanceText.hourlyPay}</TableCell>
                <TableCell className={classes.tableHeader} style={{ fontSize: 13 }} align="right">{staffPerformanceText.revenuePerHour}</TableCell>
                <TableCell className={classes.tableHeader} style={{ fontSize: 13 }} align="right">{staffPerformanceText.avgNps}</TableCell>
                <TableCell className={classes.tableHeader} style={{ fontSize: 13 }} align="left">{staffPerformanceText.staffComplaintBreakdown}</TableCell>
                <TableCell className={classes.tableHeader} style={{ fontSize: 13 }} align="right">{staffPerformanceText.completionRate}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {staffPerformance.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography className={classes.bodyText}>{staffPerformanceText.noData}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                staffPerformance.map((staff, index) => (
                  <TableRow key={index} hover>
                    <TableCell>
                      <Typography style={{ fontWeight: 600, color: '#002D55', fontSize: 13 }}>
                        {staff.staff_name}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography className={classes.bodyText}>{staff.total_appointments || 0}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography style={{ fontWeight: 600, color: '#002D55', fontSize: 13 }}>
                        {formatCurrency(staff.total_revenue || 0)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography style={{ fontWeight: 600, color: '#002D55', fontSize: 13 }}>
                        {(() => {
                          const details = getStaffAnalyticsDetails(staff);
                          return details.hourlyPay != null ? formatCurrency(details.hourlyPay, analyticsKpis?.currency || 'DKK') : '—';
                        })()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography style={{ fontWeight: 600, color: '#002D55', fontSize: 13 }}>
                        {(() => {
                          const details = getStaffAnalyticsDetails(staff);
                          return details.revenuePerWorkedHour != null ? formatCurrency(details.revenuePerWorkedHour, analyticsKpis?.currency || 'DKK') : '—';
                        })()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography style={{ fontWeight: 600, color: '#002D55', fontSize: 13 }}>
                        {staff.avg_nps != null ? Number(staff.avg_nps).toFixed(1) : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {(staff.complaint_nps_breakdown || []).length > 0 ? (
                        <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {(staff.complaint_nps_breakdown || []).map((bucket) => (
                            <Chip
                              key={`${staff.staff_id}-${bucket.primary_complaint_key}`}
                              size="small"
                              variant="outlined"
                              style={{ borderColor: '#d7e2ef', backgroundColor: '#f7faff', color: '#002D55', fontSize: 11 }}
                              label={`${bucket.primary_complaint_label}: ${bucket.avg_nps != null ? Number(bucket.avg_nps).toFixed(1) : '—'} (${bucket.count || 0})`}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography className={classes.bodyText}>{staffPerformanceText.noComplaintBreakdown}</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography style={{ color: staff.completion_rate >= 80 ? '#4caf50' : '#ff9800', fontSize: 13 }}>
                        {staff.completion_rate ? `${staff.completion_rate.toFixed(1)}%` : '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Patient Acquisition */}
        <Divider style={{ marginTop: 24, marginBottom: 16 }} />
        <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <PeopleIcon style={{ color: '#002D55' }} />
          <Typography className={classes.sectionTitle} style={{ fontSize: 18, marginBottom: 0 }}>Patient Acquisition Trends</Typography>
        </Box>
        <Grid container spacing={2}>
          {patientAcquisition.length === 0 ? (
            <Grid item xs={12}>
              <Typography className={classes.bodyText} align="center">No patient data available</Typography>
            </Grid>
          ) : (
            patientAcquisition.slice(-6).map((month, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box className={classes.statCard}>
                  <Typography className={classes.bodyText}>{month.month}</Typography>
                  <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 22, marginTop: 4 }}>
                    {month.new_patients || 0}
                  </Typography>
                  <Typography style={{ color: '#4caf50', fontSize: 12 }}>New Patients</Typography>
                </Box>
              </Grid>
            ))
          )}
        </Grid>

        {/* Support Tickets */}
        <Divider style={{ marginTop: 24, marginBottom: 16 }} />
        <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ContactSupportIcon style={{ color: '#002D55' }} />
            <Typography className={classes.sectionTitle} style={{ fontSize: 18, marginBottom: 0 }}>Support Tickets</Typography>
          </Box>
          <Button
            variant="contained"
            className={classes.primaryButton}
            onClick={() => setTicketDialogOpen(true)}
          >
            New Ticket
          </Button>
        </Box>
        <TableContainer component='div' style={{ borderRadius: 18, overflow: 'hidden', border: '1px solid #d7e2ef' }}>
          <Table>
            <TableHead>
              <TableRow style={{ backgroundColor: '#F0F8FF' }}>
                <TableCell className={classes.tableHeader} style={{ fontSize: 13 }}>Subject</TableCell>
                <TableCell className={classes.tableHeader} style={{ fontSize: 13 }}>Priority</TableCell>
                <TableCell className={classes.tableHeader} style={{ fontSize: 13 }}>Status</TableCell>
                <TableCell className={classes.tableHeader} style={{ fontSize: 13 }}>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {supportTickets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography className={classes.bodyText}>No support tickets</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                supportTickets.map((ticket) => (
                  <TableRow key={ticket.id} hover>
                    <TableCell>
                      <Typography style={{ fontSize: 13, color: '#002D55' }}>{ticket.subject}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ticket.priority?.toUpperCase()}
                        size="small"
                        style={{ backgroundColor: getPriorityColor(ticket.priority), color: '#ffffff', fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip label={ticket.status?.toUpperCase()} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography className={classes.bodyText}>
                        {new Date(ticket.created_at).toLocaleDateString('da-DK')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Create Support Ticket Dialog */}
        <Dialog open={ticketDialogOpen} onClose={() => setTicketDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile} PaperProps={{ className: classes.dialogPaper }}>
          <DialogTitle className={classes.dialogTitle}>
            <Box className={classes.dialogTitleRow}>
              <Typography className={classes.dialogTitleText}>Create Support Ticket</Typography>
              <IconButton onClick={() => setTicketDialogOpen(false)} className={classes.closeButton} size="small">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>
            <TextField
              fullWidth
              label="Subject"
              value={newTicket.subject}
              onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
              variant="outlined"
              margin="normal"
              className={classes.orangeFocused}
            />
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Description"
              value={newTicket.description}
              onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
              variant="outlined"
              margin="normal"
              className={classes.orangeFocused}
            />
            <TextField
              select
              fullWidth
              label="Priority"
              value={newTicket.priority}
              onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
              variant="outlined"
              margin="normal"
              className={classes.orangeFocused}
            >
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
            </TextField>
            <TextField
              select
              fullWidth
              label="Category"
              value={newTicket.category}
              onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value })}
              variant="outlined"
              margin="normal"
              className={classes.orangeFocused}
            >
              <MenuItem value="general">General</MenuItem>
              <MenuItem value="billing">Billing</MenuItem>
              <MenuItem value="technical">Technical</MenuItem>
              <MenuItem value="feature_request">Feature Request</MenuItem>
            </TextField>
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            <Button onClick={() => setTicketDialogOpen(false)} className={classes.secondaryButton} classes={{ root: classes.dialogActionButton }}>Cancel</Button>
            <Button onClick={handleCreateTicket} className={classes.primaryButton} variant="contained" classes={{ root: classes.dialogActionButton }}>
              Create Ticket
            </Button>
          </DialogActions>
        </Dialog>
    </Box>
  );
};

export default CRMProviderDashboard;
